/* RavenTorch - interaction layer. Vanilla JS, no dependencies.
 *
 * Animation budget: transform and opacity only. Most traffic will arrive from
 * TikTok on a mid-range phone, so anything that forces layout during scroll is
 * off the table.
 *
 * Failure policy: the head snippet drops the `rt-js` gate after 2.6s unless this
 * file signals ready. Every hiding rule sits behind that gate, so a script that
 * never runs costs an animation, never a section.
 */
(function () {
  'use strict';

  var root = document.documentElement;
  var reduced = false;
  try { reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) {}
  var hasIO = 'IntersectionObserver' in window;

  function safe(fn) {
    try { fn(); } catch (e) { if (window.console) console.warn('[rt]', e); }
  }

  function showAll() {
    document.querySelectorAll('[data-rt-reveal],[data-rt-stagger]').forEach(function (n) {
      n.classList.add('is-in');
    });
    root.classList.remove('rt-js');
  }

  /* ---------------- reveals ---------------- */
  function initReveals() {
    var nodes = document.querySelectorAll('[data-rt-reveal],[data-rt-stagger]');
    if (!nodes.length) return;
    if (reduced || !hasIO) { showAll(); return; }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });

    nodes.forEach(function (n) { io.observe(n); });

    /* Belt and braces: anything already on screen reveals on the next frame
       rather than waiting for the observer's first callback. */
    requestAnimationFrame(function () {
      nodes.forEach(function (n) {
        var r = n.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) n.classList.add('is-in');
      });
    });
    setTimeout(showAll, 6000);
  }

  /* ---------------- header ---------------- */
  function initHeader() {
    var head = document.querySelector('[data-rt-head]');
    if (!head) return;
    var menu = document.querySelector('[data-rt-menu]');
    var burger = head.querySelector('[data-rt-burger]');
    var stuck = -1;

    function onScroll() {
      var s = window.scrollY > 24 ? 1 : 0;
      if (s === stuck) return;
      stuck = s;
      head.classList.toggle('is-stuck', !!s);
    }
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    if (burger && menu) {
      burger.addEventListener('click', function () {
        var open = head.classList.toggle('is-open');
        menu.classList.toggle('is-open', open);
        burger.setAttribute('aria-expanded', open ? 'true' : 'false');
        document.body.classList.toggle('rt-body-lock', open);
      });
      menu.querySelectorAll('a').forEach(function (a) {
        a.addEventListener('click', function () {
          head.classList.remove('is-open');
          menu.classList.remove('is-open');
          burger.setAttribute('aria-expanded', 'false');
          document.body.classList.remove('rt-body-lock');
        });
      });
    }
  }

  /* ---------------- hero: product follows the pointer ----------------
     Desktop only, and only a few pixels. The effect should register as depth,
     not as a toy. Pointer coordinates are cached and applied on a frame so a
     fast mouse cannot queue up work. */
  function initHeroTilt() {
    if (reduced) return;
    var shot = document.querySelector('[data-rt-tilt]');
    if (!shot) return;
    if (!window.matchMedia('(min-width: 900px) and (hover: hover)').matches) return;

    var tx = 0, ty = 0, queued = false;
    function apply() {
      queued = false;
      shot.style.setProperty('--rt-px', tx.toFixed(2) + 'px');
      shot.style.setProperty('--rt-py', ty.toFixed(2) + 'px');
    }
    window.addEventListener('mousemove', function (e) {
      tx = ((e.clientX / window.innerWidth) - 0.5) * 26;
      ty = ((e.clientY / window.innerHeight) - 0.5) * 18;
      if (!queued) { queued = true; requestAnimationFrame(apply); }
    }, { passive: true });
  }

  /* ---------------- parallax ----------------
     Drives a CSS custom property from rAF; the browser composites the transform.
     Only elements currently on screen are in the loop. */
  function initParallax() {
    if (reduced || !hasIO) return;
    var nodes = document.querySelectorAll('[data-rt-parallax]');
    if (!nodes.length) return;

    var live = [];
    var raf = null;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        var i = live.indexOf(e.target);
        if (e.isIntersecting && i === -1) live.push(e.target);
        if (!e.isIntersecting && i > -1) live.splice(i, 1);
      });
      if (live.length && raf === null) raf = requestAnimationFrame(tick);
    }, { rootMargin: '25% 0px 25% 0px' });

    nodes.forEach(function (n) { io.observe(n); });

    function tick() {
      raf = null;
      var vh = window.innerHeight;
      for (var i = 0; i < live.length; i++) {
        var el = live[i];
        var r = el.getBoundingClientRect();
        var p = ((r.top + r.height / 2) - vh / 2) / (vh / 2 + r.height / 2);
        if (p > 1) p = 1; else if (p < -1) p = -1;
        var k = parseFloat(el.getAttribute('data-rt-parallax')) || 0.1;
        el.style.setProperty('--rt-py', (p * r.height * k * 0.5).toFixed(2) + 'px');
      }
      if (live.length) raf = requestAnimationFrame(tick);
    }

    window.addEventListener('scroll', function () {
      if (raf === null && live.length) raf = requestAnimationFrame(tick);
    }, { passive: true });
  }

  /* ---------------- count-up ----------------
     The lumen figure counts to its real value. It starts at the final number in
     the markup, so with no JavaScript the correct figure is already on screen. */
  function initCount() {
    if (reduced || !hasIO) return;
    document.querySelectorAll('[data-rt-count]').forEach(function (el) {
      var target = parseInt(el.getAttribute('data-rt-count'), 10);
      if (!isFinite(target)) return;
      var done = false;
      new IntersectionObserver(function (entries, obs) {
        entries.forEach(function (e) {
          if (!e.isIntersecting || done) return;
          done = true;
          obs.disconnect();
          var t0 = null;
          var dur = 1400;
          function step(ts) {
            if (t0 === null) t0 = ts;
            var p = Math.min(1, (ts - t0) / dur);
            var eased = 1 - Math.pow(1 - p, 3);
            el.textContent = String(Math.round(target * eased));
            if (p < 1) requestAnimationFrame(step);
            else el.textContent = String(target);
          }
          el.textContent = '0';
          requestAnimationFrame(step);
        });
      }, { threshold: 0.4 }).observe(el);
    });
  }

  /* ---------------- FAQ ---------------- */
  function initFaq() {
    document.querySelectorAll('[data-rt-faq]').forEach(function (list) {
      list.querySelectorAll('[data-rt-faq-q]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var item = btn.closest('[data-rt-faq-item]');
          var panel = item.querySelector('[data-rt-faq-a]');
          var open = item.classList.contains('is-open');

          list.querySelectorAll('[data-rt-faq-item].is-open').forEach(function (other) {
            if (other === item) return;
            other.classList.remove('is-open');
            other.querySelector('[data-rt-faq-a]').style.height = '0px';
            other.querySelector('[data-rt-faq-q]').setAttribute('aria-expanded', 'false');
          });

          if (open) {
            panel.style.height = '0px';
            item.classList.remove('is-open');
            btn.setAttribute('aria-expanded', 'false');
          } else {
            item.classList.add('is-open');
            panel.style.height = panel.scrollHeight + 'px';
            btn.setAttribute('aria-expanded', 'true');
          }
        });
      });
    });
  }

  /* ---------------- gallery ---------------- */
  function initGallery() {
    document.querySelectorAll('[data-rt-gallery]').forEach(function (g) {
      var main = g.querySelector('[data-rt-gallery-main]');
      g.querySelectorAll('[data-rt-gallery-thumb]').forEach(function (t) {
        t.addEventListener('click', function () {
          if (!main) return;
          main.src = t.getAttribute('data-full');
          main.srcset = '';
          g.querySelectorAll('[data-rt-gallery-thumb]').forEach(function (o) { o.classList.remove('is-active'); });
          t.classList.add('is-active');
        });
      });
    });
  }

  /* ---------------- sticky buy bar ---------------- */
  function initSticky() {
    var bar = document.querySelector('[data-rt-stick]');
    var anchor = document.querySelector('[data-rt-stick-anchor]');
    if (!bar || !anchor || !hasIO) return;
    new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        bar.classList.toggle('is-visible', !e.isIntersecting && e.boundingClientRect.top < 0);
      });
    }, { threshold: 0 }).observe(anchor);
  }

  window.rtBoot = function () {
    window.__rtReady = true;
    safe(initReveals);
    safe(initHeader);
    safe(initHeroTilt);
    safe(initParallax);
    safe(initCount);
    safe(initFaq);
    safe(initGallery);
    safe(initSticky);
    safe(function () { if (window.rtInitBuy) window.rtInitBuy(); });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', window.rtBoot);
  } else {
    window.rtBoot();
  }

  document.addEventListener('shopify:section:load', function () { safe(window.rtBoot); });
})();
